"use client";

import { useTheaterOverlayFrozen } from "@/components/feature/theater-overlay-freeze";
import { useFeaturedBanners } from "@/hooks/useMetadata";
import { cn } from "@/lib/utils";
import type { FeaturedBanner as FeaturedBannerType } from "@/types/database";
import useEmblaCarousel from "embla-carousel-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { FeaturedBanner } from "./featured-banner";

interface FeaturedBannersProps {
  className?: string;
  limit?: number;
  showHeading?: boolean;
  /** When provided, use these banners instead of fetching. Used for page-specific banners (e.g. site_pages). */
  banners?: FeaturedBannerType[] | null;
}

export function FeaturedBanners({
  className = "",
  limit = 10,
  showHeading = false,
  banners: bannersProp,
}: FeaturedBannersProps) {
  const usePropBanners = bannersProp !== undefined && bannersProp !== null;
  const { data: fetchedBanners = [], isLoading: fetching } = useFeaturedBanners({
    enabled: !usePropBanners,
  });
  const banners = usePropBanners ? bannersProp : fetchedBanners;
  const loading = usePropBanners ? false : fetching;

  const limitedBanners = banners.slice(0, limit);
  const bannerIdsKey = limitedBanners.map((b) => b.id).join("|");

  /** False until Embla has applied scroll transforms — avoids a 1-frame flex layout showing all slides. */
  const [emblaReady, setEmblaReady] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useLayoutEffect(() => {
    setEmblaReady(false);
    setCurrentIndex(0);
  }, [bannerIdsKey]);

  // Autoplay settings
  const autoplayRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const resumeAutoplayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoplayDelay = 10000; // 10 seconds between slides
  const overlayFrozen = useTheaterOverlayFrozen();
  const overlayFrozenRef = useRef(overlayFrozen);
  overlayFrozenRef.current = overlayFrozen;

  // Set up the carousel
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "center",
    containScroll: "trimSnaps",
    dragFree: false,
    skipSnaps: false,
    duration: 45,
  });

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCurrentIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  // Set up autoplay
  const startAutoplay = useCallback(() => {
    if (autoplayRef.current || !emblaApi || overlayFrozenRef.current) return;

    autoplayRef.current = setInterval(() => {
      if (emblaApi.canScrollNext()) {
        emblaApi.scrollNext();
      } else {
        emblaApi.scrollTo(0);
      }
    }, autoplayDelay);
  }, [emblaApi]);

  const stopAutoplay = useCallback(() => {
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
      autoplayRef.current = null;
    }
    if (resumeAutoplayTimeoutRef.current) {
      clearTimeout(resumeAutoplayTimeoutRef.current);
      resumeAutoplayTimeoutRef.current = null;
    }
  }, []);

  // Embla init + autoplay
  useEffect(() => {
    if (!emblaApi) return;

    const markReady = () => setEmblaReady(true);
    emblaApi.on("init", markReady);
    emblaApi.on("reInit", markReady);

    // `init` is emitted on a microtask; if this effect runs late, still mark ready once the engine exists.
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        try {
          if (emblaApi.scrollSnapList().length > 0) {
            markReady();
          }
        } catch {
          /* not wired yet */
        }
      });
    });

    // Start autoplay if we have multiple banners
    if (limitedBanners.length > 1) {
      startAutoplay();
    }

    const handlePointerDown = () => {
      stopAutoplay();
    };

    const handlePointerUp = () => {
      resumeAutoplayTimeoutRef.current = setTimeout(startAutoplay, 3000);
    };

    emblaApi.on("select", onSelect);
    emblaApi.on("pointerDown", handlePointerDown);
    emblaApi.on("pointerUp", handlePointerUp);
    onSelect();

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      emblaApi.off("init", markReady);
      emblaApi.off("reInit", markReady);
      emblaApi.off("select", onSelect);
      emblaApi.off("pointerDown", handlePointerDown);
      emblaApi.off("pointerUp", handlePointerUp);
      stopAutoplay();
    };
  }, [emblaApi, limitedBanners.length, onSelect, startAutoplay, stopAutoplay]);

  useEffect(() => {
    if (overlayFrozen) {
      stopAutoplay();
      return;
    }
    if (emblaApi && limitedBanners.length > 1) {
      startAutoplay();
    }
  }, [overlayFrozen, emblaApi, limitedBanners.length, startAutoplay, stopAutoplay]);

  // If no banners and not loading, don't render anything
  if (!loading && limitedBanners.length === 0) {
    return null;
  }

  // Special case: only one banner (no carousel needed)
  if (!loading && limitedBanners.length === 1) {
    return (
      <div className={`py-4 ${className}`}>
        {showHeading && (
          <h2 className="text-2xl font-bold text-brand-purple mb-6">Featured Games</h2>
        )}

        <div className="flex justify-center animate-fade-scale-in">
          <FeaturedBanner banner={limitedBanners[0]} className="w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      {showHeading && <h2 className="text-2xl font-bold text-brand-purple mb-6">Featured Games</h2>}

      {loading ? (
        // Loading state - single placeholder
        <div className="flex justify-center">
          <div
            className="rounded-3xl bg-brand-purple relative w-full overflow-hidden p-0"
            style={{ aspectRatio: "960/320" }}
          >
            <div className="w-full h-full shimmer-gradient" />
          </div>
        </div>
      ) : (
        <div className="relative w-full">
          {/* Cover pre-Embla layout: flex briefly lays out all slides side-by-side (~33% each). */}
          <div
            className={cn(
              "transition-opacity duration-150 motion-reduce:transition-none",
              emblaReady ? "visible opacity-100" : "invisible opacity-0"
            )}
            aria-hidden={!emblaReady}
          >
            <div className="embla overflow-hidden p-0" ref={emblaRef}>
              <div className="embla__container flex touch-pan-y">
                {limitedBanners.map((banner, index) => (
                  <div key={banner.id} className="embla__slide relative w-full">
                    <div
                      className="transition-all duration-500 ease-out"
                      style={{
                        opacity: index === currentIndex ? 1 : 0.4,
                        transform: index === currentIndex ? "scale(1)" : "scale(0.9)",
                      }}
                    >
                      <FeaturedBanner banner={banner} className="w-full" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {!emblaReady && limitedBanners[0] && (
            <div className="pointer-events-none absolute inset-x-0 top-0 z-[1] w-full">
              <FeaturedBanner banner={limitedBanners[0]} className="w-full" />
            </div>
          )}
        </div>
      )}

      <style jsx>{`
        .shimmer-gradient {
          position: relative;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, rgb(var(--brand-purple) / 0.9) 0%, rgb(var(--brand-indigo-soft) / 0.15) 50%, rgb(var(--brand-purple) / 0.9) 100%);
          background-size: 200% 100%;
          animation: shimmer 1.8s infinite linear;
          border-radius: 1.5rem;
        }

        @keyframes shimmer {
          0% {
            background-position: -100% 0;
          }
          100% {
            background-position: 100% 0;
          }
        }

        @keyframes fadeScaleIn {
          0% {
            opacity: 0;
            transform: scale(0.97);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-fade-scale-in {
          animation: fadeScaleIn 2.8s ease-out forwards;
        }

        /* Base embla styles */
        .embla {
          --slide-spacing: 0;
          --slide-size: 100%;
          --slide-height: 19rem;
          padding: 0;
          will-change: transform;
        }

        .embla__container {
          backface-visibility: hidden;
          display: flex;
          touch-action: pan-y;
          margin: 0;
          width: 100%;
          will-change: transform;
          -webkit-transform-style: preserve-3d;
          transform-style: preserve-3d;
        }

        .embla__slide {
          will-change: transform;
          -webkit-backface-visibility: hidden;
          backface-visibility: hidden;
          width: 100%;
          flex: 0 0 100%;
          min-width: 0;
        }
      `}</style>
    </div>
  );
}
